import React, { Component, ReactNode } from 'react';
import { logger } from '../../utils/logger';

interface RealtimeErrorBoundaryState {
  hasError: boolean;
  error?: Error;
  errorInfo?: React.ErrorInfo;
  retryCount: number;
}

interface RealtimeErrorBoundaryProps {
  children: ReactNode;
  /** Maximum number of automatic retries (default: 3) */
  maxRetries?: number;
  /** Fallback component to render when subscription fails */
  fallback?: React.ComponentType<{ error: Error; retry: () => void }>;
  /** Callback when subscription errors occur */
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
}

/**
 * Error boundary specifically designed for realtime subscription failures
 *
 * This component catches errors in subscription setup and provides
 * automatic retry functionality with exponential backoff.
 */
export class RealtimeErrorBoundary extends Component<
  RealtimeErrorBoundaryProps,
  RealtimeErrorBoundaryState
> {
  private retryTimeoutId: NodeJS.Timeout | null = null;

  constructor(props: RealtimeErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      retryCount: 0,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<RealtimeErrorBoundaryState> {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    const { onError, maxRetries = 3 } = this.props;
    const { retryCount } = this.state;

    // Log the error with context
    logger.error('Realtime subscription error caught by boundary', {
      error: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      retryCount,
      maxRetries,
    });

    // Update state with error info
    this.setState({
      errorInfo,
    });

    // Call custom error handler if provided
    if (onError) {
      onError(error, errorInfo);
    }

    // Automatically retry if under the limit
    if (retryCount < maxRetries) {
      this.scheduleRetry();
    }
  }

  private scheduleRetry = () => {
    const { retryCount } = this.state;

    // Clear any existing retry timeout
    if (this.retryTimeoutId) {
      clearTimeout(this.retryTimeoutId);
    }

    // Calculate exponential backoff delay (1s, 2s, 4s, etc.)
    const delay = Math.pow(2, retryCount) * 1000;

    logger.realtime('Scheduling subscription retry', {
      retryCount: retryCount + 1,
      delay,
    });

    this.retryTimeoutId = setTimeout(() => {
      this.setState(prevState => ({
        hasError: false,
        error: undefined,
        errorInfo: undefined,
        retryCount: prevState.retryCount + 1,
      }));
    }, delay);
  };

  private handleManualRetry = () => {
    logger.realtime('Manual subscription retry triggered');

    this.setState({
      hasError: false,
      error: undefined,
      errorInfo: undefined,
      retryCount: 0, // Reset retry count for manual retries
    });
  };

  componentWillUnmount() {
    // Clear retry timeout on unmount
    if (this.retryTimeoutId) {
      clearTimeout(this.retryTimeoutId);
    }
  }

  render() {
    const { hasError, error } = this.state;
    const { children, fallback: FallbackComponent } = this.props;

    if (hasError && error) {
      // Use custom fallback component if provided
      if (FallbackComponent) {
        return <FallbackComponent error={error} retry={this.handleManualRetry} />;
      }

      // Default fallback UI
      return (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 m-4">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Realtime Connection Error</h3>
              <p className="mt-1 text-sm text-red-700">
                Unable to establish realtime connection. The app will continue to work, but changes
                may not appear immediately.
              </p>
              <div className="mt-3">
                <button
                  onClick={this.handleManualRetry}
                  className="bg-red-100 text-red-800 px-3 py-2 rounded text-sm font-medium hover:bg-red-200 transition-colors"
                >
                  Retry Connection
                </button>
              </div>
              {process.env.NODE_ENV === 'development' && (
                <details className="mt-3">
                  <summary className="text-xs text-red-600 cursor-pointer">
                    Error Details (Dev Only)
                  </summary>
                  <pre className="mt-2 text-xs text-red-600 whitespace-pre-wrap">
                    {error.message}
                    {error.stack && `\n\n${error.stack}`}
                  </pre>
                </details>
              )}
            </div>
          </div>
        </div>
      );
    }

    return children;
  }
}

/**
 * HOC to wrap components with realtime error boundary
 */
export function withRealtimeErrorBoundary<P extends object>(
  WrappedComponent: React.ComponentType<P>,
  errorBoundaryProps?: Omit<RealtimeErrorBoundaryProps, 'children'>
) {
  const ComponentWithErrorBoundary = (props: P) => (
    <RealtimeErrorBoundary {...errorBoundaryProps}>
      <WrappedComponent {...props} />
    </RealtimeErrorBoundary>
  );

  ComponentWithErrorBoundary.displayName = `withRealtimeErrorBoundary(${WrappedComponent.displayName || WrappedComponent.name})`;

  return ComponentWithErrorBoundary;
}

/**
 * Custom fallback component for subscription errors
 */
export const MinimalRealtimeFallback: React.FC<{ error: Error; retry: () => void }> = ({
  retry,
}) => (
  <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3 mb-4">
    <div className="flex items-center">
      <div className="flex-shrink-0">
        <svg className="h-4 w-4 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
          <path
            fillRule="evenodd"
            d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
            clipRule="evenodd"
          />
        </svg>
      </div>
      <div className="ml-2">
        <p className="text-sm text-yellow-700">
          Realtime updates temporarily unavailable.
          <button
            onClick={retry}
            className="ml-2 text-yellow-800 underline hover:no-underline text-sm"
          >
            Retry
          </button>
        </p>
      </div>
    </div>
  </div>
);
